'use client'
import { useEffect, useRef } from 'react';

const Block = ({ children, delay }) => {
  const blockRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          blockRef.current.classList.add('animate-fold-in');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (blockRef.current) {
      observer.observe(blockRef.current);
    }

    return () => {
      if (blockRef.current) {
        observer.unobserve(blockRef.current);
      }
    };
  }, []);

  return (
    <div 
      ref={blockRef}
      className="opacity-0 -rotate-y-90 origin-left"
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const BlockSection = () => {
  const blocks = [
    {
      title: "Rich Text Editor",
      description: "Full-featured Markdown editor with support for tables, code blocks, and more.",
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />,
      colorClasses: "bg-indigo-500/10 text-indigo-400"
    },
    {
      title: "Video Embed",
      description: "Seamlessly embed YouTube videos with custom thumbnails and captions.",
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />,
      colorClasses: "bg-sky-500/10 text-sky-400"
    },
    {
      title: "Smart Links",
      description: "Beautiful link previews with automatic favicon and metadata extraction.",
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />,
      colorClasses: "bg-purple-500/10 text-purple-400"
    },
    {
      title: "Kanban Boards",
      description: "Visual project management with drag-and-drop task organization.",
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />,
      colorClasses: "bg-emerald-500/10 text-emerald-400"
    },
    {
      title: "Image Gallery",
      description: "Create beautiful image galleries with automatic optimization and lazy loading.",
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />,
      colorClasses: "bg-rose-500/10 text-rose-400"
    },
    {
      title: "Affiliate Links",
      description: "Organize and manage your affiliate links with automatic tracking and analytics.",
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />,
      colorClasses: "bg-amber-500/10 text-amber-400"
    },
    {
      title: "Timeline View",
      description: "Present your content in a chronological timeline with rich media support.",
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />,
      colorClasses: "bg-fuchsia-500/10 text-fuchsia-400"
    },
    {
      title: "Code Snippets",
      description: "Share code with syntax highlighting and copy-to-clipboard functionality.",
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />,
      colorClasses: "bg-cyan-500/10 text-cyan-400"
    },
    {
      title: "More Coming Soon",
      description: "We are constantly adding new blocks to help you tell your story better.",
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />,
      colorClasses: "bg-violet-500/10 text-violet-400"
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {blocks.map((block, index) => (
        <div
          key={index}
          className="relative group bg-white/5 p-6 rounded-xl border border-white/10 hover:border-white/20 transition-colors"
        >
          <div className={`flex items-center justify-center h-12 w-12 rounded-md ${block.colorClasses} mb-4`}>
            <svg
              className="h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {block.icon}
            </svg>
          </div>
          <h3 className="text-lg font-medium text-white mb-2">{block.title}</h3>
          <p className="text-gray-400">{block.description}</p>
        </div>
      ))}
    </div>
  );
};

export default BlockSection; 